/**
 * Zustand ストアと pipeline/run-all を結ぶフック。
 * UI 側から `start()` を呼ぶと、progress を Zustand に反映しつつ ZIP を生成する。
 */

import { useCallback } from 'react';

import { runPipeline } from '../lib/pipeline/run-all';
import { toVehicleMetadata, useAppStore } from '../stores/useAppStore';
import { validateMetadata } from '../stores/metadata-validation';

export function usePipeline(): {
  readonly start: () => Promise<void>;
  readonly canStart: boolean;
} {
  const files = useAppStore((s) => s.files);
  const heroIndex = useAppStore((s) => s.heroIndex);
  const metadataInput = useAppStore((s) => s.metadata);
  const phase = useAppStore((s) => s.phase);
  const setPhase = useAppStore((s) => s.setPhase);
  const setErrors = useAppStore((s) => s.setErrors);

  const canStart = phase.kind === 'idle' && files.length > 0 && files.length <= 80;

  const start = useCallback(async (): Promise<void> => {
    if (!canStart) return;

    // バリデーション
    setPhase({ kind: 'validating' });
    const validation = validateMetadata(metadataInput);
    if (!validation.ok) {
      setErrors(Object.values(validation.errors).filter((v): v is string => Boolean(v)));
      setPhase({ kind: 'idle' });
      return;
    }
    setErrors([]);

    const metadata = toVehicleMetadata(metadataInput);

    try {
      const result = await runPipeline({
        files,
        heroIndex,
        metadata,
        onProgress: (p) =>
          setPhase({
            kind: 'processing',
            step: p.step,
            current: p.current,
            total: p.total,
          }),
      });
      setPhase({
        kind: 'done',
        zipBlob: result.zipBlob,
        zipName: result.zipName,
        summary: {
          totalPhotos: result.totalPhotos,
          heroFallbackUsed: result.heroFallbackUsed,
          cleanedCount: result.cleanedCount,
          elapsedMs: result.elapsedMs,
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('[pipeline] failed', err);
      setPhase({ kind: 'error', message });
    }
  }, [canStart, files, heroIndex, metadataInput, setPhase, setErrors]);

  return { start, canStart };
}
